import { ApiHolder, useApi, useApiHolder } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { CircularProgress, Grid, TextField } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { FieldValidation } from '@rjsf/utils';

import { useAsyncFn } from 'react-use';
import { UnifiedGithubApi, unifiedGithubApiRef } from '../../services';
import { useState } from 'react';
import { GithubOrgPrefix } from '../../components/GithubOrgPrefix';
import { getGithubDestination } from './ComponentInformation/githubDestination';

const forbiddenNpmNames = ['http', 'stream', 'node_modules', 'favicon.ico'];

const isPullRequestValidation = (validationType?: string) =>
  validationType?.replace(/[-_\s]/g, '').toLowerCase() === 'pullrequest';

export const validateGithubRepoName = async (
  repo: string,
  customRegexPattern: string,
  unifiedGithubApi: Pick<UnifiedGithubApi, 'isUniqueRepo'>,
  validationType: string,
  repoLookup?: Awaited<ReturnType<UnifiedGithubApi['lookupRepo']>>,
) => {
  const checkingPullRequestRepository = isPullRequestValidation(validationType);

  if (repo === '') {
    throw new Error('Cannot have empty repo');
  }
  if (customRegexPattern === 'npmRegex') {
    if (repo.length > 214) {
      throw new Error('Package name length cannot exceed 214 characters');
    }
    if (repo !== repo.toLowerCase()) {
      throw new Error('all the characters in the package name must be lowercase');
    }
    if (forbiddenNpmNames.includes(repo)) {
      throw new Error(`package name cannot be the same as a node.js/io.js core module nor a reserved/blacklisted name`);
    }
    if (repo.startsWith('.') || repo.startsWith('_')) {
      throw new Error('Package name cannot start with a period or underscore');
    }
    if (/\s/.test(repo)) {
      throw new Error('Package name cannot contain spaces');
    }
    if (/[~()\!'*]/.test(repo)) {
      throw new Error("Package name cannot contain any of the following characters: ~()'!*");
    }
  }

  const isUniqueRepoNameResp = repoLookup
    ? !repoLookup.exists
    : await unifiedGithubApi.isUniqueRepo({ repoName: repo });
  if (isUniqueRepoNameResp) {
    if (checkingPullRequestRepository) {
      throw new Error('Repo does not exist for Pull Request!');
    }
    return 'Repo Name is unique!';
  } else if (checkingPullRequestRepository) {
    return 'Repo exists for Pull Request!';
  }
  throw new Error('Repo Name is not unique! Cannot create repo');
};

export const GithubRepoField = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  uiSchema,
}: FieldExtensionComponentProps<string>) => {
  const unifiedGithubApi = useApi(unifiedGithubApiRef);
  const apiHolder = useApiHolder();
  const configuredOrganization = uiSchema?.['ui:options']?.githubOrganization;
  const githubDestination = getGithubDestination(configuredOrganization, apiHolder);
  const destination = githubDestination.status === 'resolved' ? githubDestination.destination : undefined;
  const destinationError = githubDestination.status === 'error' ? githubDestination.message : undefined;
  const validationType = (schema as any)?.validation;
  const customRegexPattern = (schema as any)?.pattern;

  const [showNpmRules, setShowNpmRules] = useState(false);

  const [validateRepoNameState, validateRepoName] = useAsyncFn(
    async (repo: string) => {
      if (!destination) {
        throw new Error(destinationError);
      }
      let repoLookup;
      if (!isPullRequestValidation(validationType)) {
        repoLookup = await unifiedGithubApi.lookupRepoForDestination({
          repoName: repo,
          owner: destination.organization,
          origin: destination.origin,
        });
      } else {
        repoLookup = await unifiedGithubApi.lookupRepo({
          repoName: repo,
          owner: destination.organization,
          origin: destination.origin,
        });
      }
      if (
        repoLookup.exists &&
        repoLookup.owner.toLowerCase() !== destination.organization.toLowerCase() &&
        !isPullRequestValidation(validationType)
      ) {
        throw new Error(
          `Repo Name is not unique! It already exists in ${repoLookup.owner}, and names must be unique across organizations during the migration.`,
        );
      }
      return validateGithubRepoName(repo, customRegexPattern, unifiedGithubApi, validationType, repoLookup);
    },
    [
      unifiedGithubApi,
      customRegexPattern,
      validationType,
      destination?.organization,
      destination?.origin,
      destinationError,
    ],
  );

  if (!destination) {
    return <Alert severity="error">{destinationError}</Alert>;
  }

  return (
    <Grid container>
      <Grid
        item
        xs={validateRepoNameState.loading || validateRepoNameState.value || validateRepoNameState.error ? 8 : 12}
      >
        <TextField
          id="name"
          label="Repo Name"
          helperText="Repo Name"
          style={{ width: '100%' }}
          required={required}
          value={formData ?? ''}
          onInput={() => {
            if (customRegexPattern === 'npmRegex') {
              setShowNpmRules(true);
            }
          }}
          onChange={({ target: { value } }) => {
            onChange(value);
          }}
          onBlur={({ target: { value } }) => {
            if (value) {
              validateRepoName(value);
            }
          }}
          InputProps={{
            startAdornment: <GithubOrgPrefix organization={destination.organization} />,
          }}
          error={!!((rawErrors?.length > 0 && !formData) || validateRepoNameState.error)}
        />
      </Grid>{' '}
      {showNpmRules && (
        <Grid item xs={12}>
          <Alert severity="info">
            GitHub repository name turns into a dependency to be used to name npm package, and should follow these npm
            naming rules:
            <ul>
              <li>all the characters in the package name must be lowercase.</li>
              <li>package name should not start with . or _ </li>
              <li>package name should not contain any spaces or special characters like ~)('!* </li>
              <li>
                package name cannot be the same as a node.js/io.js core module nor a reserved/blacklisted name like
                http, stream
              </li>
            </ul>
          </Alert>
        </Grid>
      )}
      {validateRepoNameState.loading && (
        <Grid item xs={4}>
          <CircularProgress style={{ padding: 5 }} />
        </Grid>
      )}
      {validateRepoNameState.error && (
        <Grid item xs={4}>
          <Alert severity="error">{validateRepoNameState.error?.message}</Alert>
        </Grid>
      )}
      {validateRepoNameState.value && !validateRepoNameState.error && (
        <Grid item xs={4}>
          <Alert severity="success">{validateRepoNameState.value}</Alert>
        </Grid>
      )}
    </Grid>
  );
};

export const githubRepoFieldValidation = (
  value: string,
  validation: FieldValidation,
  context?: { apiHolder?: ApiHolder; uiSchema?: { 'ui:options'?: Record<string, unknown> } },
) => {
  const githubDestination = getGithubDestination(
    context?.uiSchema?.['ui:options']?.githubOrganization,
    context?.apiHolder,
  );
  if (githubDestination.status === 'error') {
    validation.addError(githubDestination.message);
  }
  const regexp = new RegExp('^[a-zA-Z][a-z0-9-.A-Z_]*$');
  if (!regexp.test(value)) {
    validation.addError(
      'Please use letters, dashes, underscores, periods, and numbers. You must start and end with a letter.',
    );
  } else if (value.length > 56) {
    validation.addError('Names cannot be more than 56 characters.');
  }
};
